import React from 'react';

/**
 * Avatar Component - Atomic Level
 * 
 * Displays user avatar with fallback to initials
 * 
 * Features:
 * - Multiple sizes
 * - Image support with fallback
 * - Initials generation
 * - Status indicator
 * - Accessible
 */

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeClasses: Record<AvatarSize, { container: string; text: string }> = {
  xs: { container: 'w-6 h-6', text: 'text-xs' },
  sm: { container: 'w-8 h-8', text: 'text-sm' },
  md: { container: 'w-10 h-10', text: 'text-base' },
  lg: { container: 'w-12 h-12', text: 'text-lg' },
  xl: { container: 'w-16 h-16', text: 'text-xl' },
};

export interface AvatarProps {
  /** User's name for initials fallback */
  name: string;
  /** Avatar image URL */
  src?: string;
  /** Avatar size */
  size?: AvatarSize;
  /** Show online status indicator */
  showStatus?: boolean;
  /** Status (online/offline/away) */
  status?: 'online' | 'offline' | 'away';
  /** Custom class name */
  className?: string;
  /** Alt text for image */
  alt?: string;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const statusColors = {
  online: 'bg-green-500',
  offline: 'bg-gray-400',
  away: 'bg-yellow-500',
};

export const Avatar: React.FC<AvatarProps> = ({
  name,
  src,
  size = 'md',
  showStatus = false,
  status = 'offline',
  className = '',
  alt,
}) => {
  const [imageError, setImageError] = React.useState(false);
  const initials = getInitials(name);
  const { container, text } = sizeClasses[size];

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className={`${container} rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#EAB308] to-[#244B75] text-white font-semibold select-none`}
      >
        {src && !imageError ? (
          <img
            src={src}
            alt={alt || name}
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
        ) : (
          <span className={text}>{initials}</span>
        )}
      </div>
      
      {showStatus && (
        <span
          className={`absolute bottom-0 right-0 block w-3 h-3 rounded-full border-2 border-white ${statusColors[status]}`}
          aria-label={`Status: ${status}`}
        />
      )}
    </div>
  );
};

export default Avatar;

